import type { ActionFunction, LoaderFunction, NavigationType, RouteObject } from 'react-router'
import type { AxiosInstance } from 'axios'
import type { Store } from 'redux'
import { isAxiosError } from 'axios'
import loadable from '@loadable/component'

import {
  getDefaultThemeSettings,
  getScriptTags,
  getTax,
  getThemeSettingsSchema,
  loadFooterPages,
  loadLegalPages,
  loadNavigation,
} from './store/actions'
import { getLoadableComponent } from '../web/utils/getLoadableComponent'
import { mapDeep } from './utils/treeHelpers'
import { parseUrlQuery } from '../utils/parseUrlQuery'
import Category from './views/Category'
import ErrorBoundary from './views/ErrorBoundary'
import Home from './views/Home'
import LegalPage from './views/LegalPage'
import Order from './views/Order'
import OrderWithdrawal from './views/OrderWithdrawal'
import OrderWithdrawalConfirmation from './views/OrderWithdrawalConfirmation'
import Page from './views/Page'
import RootView from './views/RootView'
import Search from './views/Search'
import Storefront from './views/Storefront'
import UnknownRoute from './views/UnknownRoute'
import runAllViewStoreUpdates from '../web/utils/runAllViewStoreUpdates'

export const cartPath = 'cart'

export const orderPath = 'o'

const Account = loadable(() => import(/* webpackChunkName: "account" */ './views/Account'))
const Product = loadable(() => import(/* webpackChunkName: "Product" */ './views/Product'))
const Cart = loadable(() => import(/* webpackChunkName: "Cart" */ './views/Cart'))
const Checkout = loadable(() => import(/* webpackChunkName: "checkout" */ './views/Checkout'))
const ForgotPassword = loadable(() => import(/* webpackChunkName: "checkout" */ './views/ForgotPassword'))
const ResetPassword = loadable(() => import(/* webpackChunkName: "checkout" */ './views/ResetPassword'))
const Editor = loadable(() => import(/* webpackChunkName: "editor" */ './views/Editor'))
const Themes = loadable(() => import(/* webpackChunkName: "editor" */ './views/editor/Themes'))

export function generateRoutes(store: Store, api: AxiosInstance): RouteObject[] {
  const localizedStorefrontRoutes = {
    path: ':lang?',
    children: [
      {
        Component: Storefront,
        ErrorBoundary,
        loader: withLoaders(async ({ params }) => {
          const state = store.getState()
          const dispatch = store.dispatch as GlobalDispatch

          if (!state.get('themeSettingsSchema')) {
            await dispatch(getThemeSettingsSchema(state.getIn(['shop', 'themeId'])))
          }

          if (!state.get('defaultThemeSettings')) {
            await dispatch(getDefaultThemeSettings(state.getIn(['shop', 'themeId'])))
          }

          const locales: Core.Locale[] = state.getIn(['shop', 'locales']).toJS()
          const availableLanguages = locales.map(({ identifier }) => identifier.substring(0, 2))

          if (params.lang && !availableLanguages.includes(params.lang)) {
            // 404 Not Found if the lang parameter is not a valid two-letter
            // language code for the shop.
            throw new Response('Not found', { status: 404 })
          }

          return null
        }),
        children: [
          { index: true, Component: Home },

          { path: 'p/:productId', Component: Product },
          { path: 'c/*', Component: Category },
          { path: 'i/:pageSlug', Component: Page },
          {
            path: 'l/order-withdrawal',
            Component: OrderWithdrawal,
          },
          {
            path: 'l/order-withdrawal/confirmation',
            Component: OrderWithdrawalConfirmation,
          },
          { path: 'l/:legalPageSlug', Component: LegalPage },
          noSlugRoute('p'),
          noSlugRoute('c'),
          noSlugRoute('i'),
          noSlugRoute('l'),

          { path: cartPath, Component: Cart },
          { path: `${orderPath}`, Component: Order },
          { path: `${orderPath}/:orderId`, Component: Order },
          { path: 'search', Component: Search },
          {
            path: 'checkout',
            Component: Checkout,
          },
          {
            // todo refactor to using outlet
            path: 'checkout/:stepId',
            Component: Checkout,
          },
          { path: 'forgot-password', Component: ForgotPassword },
          { path: 'reset-password', Component: ResetPassword },
          { path: 'customer-account', Component: Account },
          { path: 'customer-account/:section', Component: Account },
          {
            path: '*',
            Component: UnknownRoute,
          },
        ],
      },
    ],
  }

  const routes: RouteObject[] = [
    {
      path: '/',
      Component: RootView,
      ErrorBoundary,
      loader: () => null,
      children: [
        localizedStorefrontRoutes,
        {
          id: 'editor',
          Component: Editor,
          path: 'editor',
          children: [{ path: 'themes', Component: Themes }, localizedStorefrontRoutes],
        },
      ],
    },
  ]

  return mapDeep(routes, (route) => {
    // Register a loader for each view component route.
    if (route.Component && !route.loader) {
      route.loader = withLoaders(getViewLoader(route.Component, api, store))
    }

    if (route.Component && !route.action && 'action' in route.Component) {
      route.action = withActionErrorHandling((route.Component.action as View.ActionFn)({ api, store }))
    }

    // `route.handle.Component` is used in the Editor component.
    route.handle = { ...route.handle, Component: route.Component }

    return route
  })
}

const basicNotFoundLoader = () => {
  throw Response.json({ message: 'Not Found', showBasicErrorPage: true }, { status: 404 })
}

const noSlugRoute = (path: string) => ({
  path,
  element: null,
  loader: basicNotFoundLoader,
})

/**
 * Generates a loader function for a route component that:
 *
 * 1. Calls the component's `validationLoader` (if it exists) to validate route params.
 * 2. Runs all necessary Redux store updates, including calling `storeUpdate` of the component (if it exists).
 * 3. Calls the component's `loader` (if it exists) to load data for the route.
 *
 * The generated loader function handles redirects and errors thrown in any of the above loader steps (early exit).
 *
 * Example, illustrating all three static methods in call order:
 *
 * ```tsx
 * const Product = () => { ... }
 * Product.validationLoader = (api, store) => async ({ params }) => { ... }
 * Product.storeUpdate = ({ params }, state) => [ ... ]
 * Product.loader = (api, store) => async ({ params }) => { ... }
 * ```
 *
 * @param RouteComponent The route component for which to generate the loader.
 * @param api The Axios instance to use for API calls.
 * @param store The Redux store instance.
 */
function getViewLoader(RouteComponent: React.ComponentType, api: AxiosInstance, store: Store): LoaderFunction {
  return async function ViewLoader(...args) {
    const Component = (await getLoadableComponent(RouteComponent)) as React.ComponentType & {
      validationLoader?: View.LoaderFn
      loader?: View.LoaderFn
    }

    // Run validation loader for the route component.
    // E.g. `Product.validationLoader = (api, store) => async ({ params }) => { ... }`
    // This might throw or return any route errors or return a redirect response.
    const routeComponentValidationLoader = Component.validationLoader?.(api, store)
    if (routeComponentValidationLoader) {
      const result = await routeComponentValidationLoader(...args)
      if (result instanceof Response && isRedirectResponse(result)) {
        return result
      }
    }

    // Run all necessary Redux store updates.
    // This includes calling `storeUpdate` of the route component.
    const viewStoreLoader = getViewStoreLoader(RouteComponent, store)
    await viewStoreLoader(...args)

    // Run data loader for the route component.
    // E.g. `Product.loader = (api, store) => async ({ params }) => { ... }`
    const routeComponentLoader = Component.loader?.(api, store)
    return routeComponentLoader?.(...args) ?? null
  }
}

function getViewStoreLoader(Component: RouteObject['Component'], store: Store): LoaderFunction {
  return async function ViewStoreLoader({ request, params }) {
    const url = new URL(request.url)
    const dispatch = store.dispatch as GlobalDispatch
    const state = store.getState()
    const locale = state.getIn(['shop', 'locale'])
    const routerProps: RouteComponentProps = {
      params,
      state,
      components: Component ? [Component] : [],
      location: {
        pathname: url.pathname,
        search: url.search,
        hash: url.hash,
        action: typeof window !== 'undefined' ? window.getNavigationType?.() : ('PUSH' as NavigationType),
        query: parseUrlQuery(url.search),
      },
    }

    await runAllViewStoreUpdates({
      routerProps,
      store,
      runningPreliminaryUpdates: [state.get('navigation') ? Promise.resolve() : dispatch(loadNavigation({ locale }))],
      runningIndependentUpdates: [
        state.get('legalPages') ? Promise.resolve() : dispatch(loadLegalPages({ locale })),
        state.get('footerPages') ? Promise.resolve() : dispatch(loadFooterPages({ locale })),
        state.get('scriptTags') ? Promise.resolve() : dispatch(getScriptTags()),
        state.get('tax') ? Promise.resolve() : dispatch(getTax()),
      ],
    })

    return null
  }
}

// work around not being able to call nested route loaders sequentially
// see https://github.com/remix-run/react-router/discussions/9564
// also used for error handling
function withLoaders(...loaders: LoaderFunction[]): LoaderFunction {
  return async (args) => {
    try {
      for (const loader of loaders) {
        const returnValue = await loader(args)
        if (returnValue) return returnValue
      }
      return null
    } catch (exception) {
      if (isRedirectResponse(exception)) {
        // Let React Router handle the redirect
        return exception
      } else {
        sendErrorResponse(exception)
      }
    }
  }
}

export function withActionErrorHandling(action: ActionFunction): ActionFunction {
  return async (args) => {
    try {
      return await action(args)
    } catch (exception) {
      if (isRedirectResponse(exception)) return exception
      sendErrorResponse(exception)
    }
  }
}

export function sendErrorResponse(err: unknown) {
  if (isAxiosError(err)) {
    const requestId = err.response?.headers['x-epages-requestid'] || err.response?.headers['x-b3-traceid'] || null
    throw Response.json(
      { message: err.response?.statusText || 'Internal Server Error', requestId },
      { status: err.response?.status || 500 },
    )
  } else if (err instanceof Response) {
    throw err
  }
  throw new Response('Internal Server Error', { status: 500 })
}

function isRedirectResponse(response: Response) {
  return response.status >= 300 && response.status < 400 && response.headers.has('Location')
}
